import { useEffect, useState } from "react";
import { useListContext } from "react-admin";
import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";
import type { PaymentSummaryResponse } from "./types";

export type UsePaymentModeSummaryConfig = {
  /** Passed through to getApiUrl for consistency with domain list resources. */
  apiResource: string;
  summaryPath: string;
  queryKeys: readonly string[];
  shouldFetch: (filterValues: Record<string, unknown>) => boolean;
  buildQuery: (filterValues: Record<string, unknown>) => URLSearchParams;
  parseResponse?: (json: unknown) => PaymentSummaryResponse | null;
};

const defaultParseResponse = (json: unknown): PaymentSummaryResponse | null =>
  json as PaymentSummaryResponse;

function pickFilterSlice(
  filterValues: Record<string, unknown> | undefined,
  queryKeys: readonly string[]
): Record<string, unknown> {
  const slice: Record<string, unknown> = {};
  if (!filterValues) return slice;
  for (const key of queryKeys) {
    slice[key] = filterValues[key];
  }
  return slice;
}

export function usePaymentModeSummary({
  apiResource,
  summaryPath,
  queryKeys,
  shouldFetch,
  buildQuery,
  parseResponse = defaultParseResponse,
}: UsePaymentModeSummaryConfig) {
  const { filterValues } = useListContext();
  const [summary, setSummary] = useState<PaymentSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const filterSlice = pickFilterSlice(filterValues, queryKeys);

    if (!shouldFetch(filterSlice)) {
      setSummary(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    const query = buildQuery(filterSlice);
    const url = `${getApiUrl(apiResource)}/${summaryPath}?${query.toString()}`;

    httpClient(url, { signal: controller.signal })
      .then(({ json }) => {
        if (!controller.signal.aborted) {
          setSummary(parseResponse(json));
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("Error fetching payment summary:", err);
        setLoading(false);
      });

    return () => controller.abort();
  }, [filterValues, apiResource, summaryPath, queryKeys, buildQuery, shouldFetch, parseResponse]);

  return { summary, loading };
}
